import { ApiError } from '../../identity/types';
import {
  TAX_REGISTRATION_LABELS,
  type SaveTaxRegistrationRequest,
  type TaxRegistrationResponse,
  type TaxRegistrationStatusName,
  type WorkLocationMailingAddress,
  type WorkLocationRequest,
  type WorkLocationResponse,
} from '../types';

/**
 * The server's Step 5 rules (WorkLocationService / WorkLocationTaxRegistrationRules in Identity.Api), re-stated for the in-memory mock so the
 * UI is driven by the same validation, masking and carry-forward behavior as the API. Synthetic only: nothing here is a tax rule or a rate.
 */

export const badRequest = (message: string) => new ApiError(400, message);

export function cloneLocation(location: WorkLocationResponse): WorkLocationResponse {
  return structuredClone(location);
}

const ZIP = /^\d{5}(-\d{4})?$/;
const IDENTIFIER = /^[A-Za-z0-9][A-Za-z0-9 .\-/]*$/;

export function validateMockAddress(request: WorkLocationRequest): void {
  if (!request.name.trim()) throw badRequest('Branch / Location Name is required.');
  if (!request.addressLine1.trim() || !request.city.trim() || !request.state || !request.zip || !request.country) {
    throw badRequest('Address Line 1, City, State, ZIP Code, and Country are required.');
  }
  if (!ZIP.test(request.zip)) throw badRequest('ZIP Code must be in the format 12345 or 12345-6789.');
}

export function validateMockMailing(request: WorkLocationRequest): WorkLocationMailingAddress {
  const mailing = request.mailingAddress;
  if (!mailing || mailing.sameAsPhysical) {
    return { sameAsPhysical: true, addressLine1: null, addressLine2: null, city: null, state: null, zip: null, country: null };
  }
  if (!mailing.addressLine1?.trim() || !mailing.city?.trim() || !mailing.state || !mailing.zip || !mailing.country) {
    throw badRequest('A separate mailing address needs Address Line 1, City, State, ZIP Code and Country.');
  }
  if (!ZIP.test(mailing.zip)) throw badRequest('The mailing address ZIP Code must be in the format 12345 or 12345-6789.');
  return { ...mailing, sameAsPhysical: false };
}

export function sameAddress(location: WorkLocationResponse, request: WorkLocationRequest): boolean {
  const key = (line1: string, city: string, state: string, zip: string) => [line1, city, state, zip].map((v) => v.trim().toUpperCase()).join('|');
  return key(location.addressLine1, location.city, location.state, location.zip) === key(request.addressLine1, request.city, request.state, request.zip);
}

/** "****1234": the last four characters only. */
export function mask(value: string | null | undefined): string | null {
  if (!value) return null;
  return value.length <= 4 ? '****' : `****${value.slice(-4)}`;
}

function carry(requested: string | null, previousMasked: string | null, label: string): string | null {
  if (requested === null) return previousMasked;
  const trimmed = requested.trim();
  if (trimmed === '') return null;
  if (trimmed.length > 50 || !IDENTIFIER.test(trimmed)) {
    throw badRequest(`${label} may contain only letters, numbers, spaces, dots, dashes and slashes, up to 50 characters.`);
  }
  return mask(trimmed);
}

const STATUS_BY_VALUE: Record<number, TaxRegistrationStatusName> = { 1: 'Registered', 2: 'InProgress', 3: 'NotRegistered', 4: 'NotRequired' };

export function buildMockRegistration(request: SaveTaxRegistrationRequest, previous: TaxRegistrationResponse | null): TaxRegistrationResponse {
  const status = STATUS_BY_VALUE[request.status];
  if (!status) throw badRequest('Select a registration status.');
  const earliest = '1990-01-01';
  const latest = new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().slice(0, 10);
  for (const [value, label] of [[request.effectiveDate, 'Tax Registration Effective Date'], [request.nexusEstablishedDate, 'Nexus Established Date']] as const) {
    if (value && (value < earliest || value > latest)) throw badRequest(`${label} must be between 01/01/1990 and one year from today.`);
  }
  const notes = request.notes?.trim() ? request.notes.trim() : null;
  if (notes && notes.length > 100) throw badRequest('Notes / Compliance Remarks must be 100 characters or fewer.');

  const state = carry(request.stateAccountNumber, previous?.stateAccountNumberMasked ?? null, 'State account number (SEIN)');
  const sui = carry(request.suiAccountNumber, previous?.suiAccountNumberMasked ?? null, 'SUI account number');
  const sdi = carry(request.sdiPflAccountNumber, previous?.sdiPflAccountNumberMasked ?? null, 'SDI / PFL account number');
  const localReg = carry(request.localTaxRegistration, previous?.localTaxRegistrationMasked ?? null, 'Local tax registration');
  const localAcct = carry(request.localTaxAccountNumber, previous?.localTaxAccountNumberMasked ?? null, 'Local tax account number');

  if (status === 'Registered') {
    if (!request.effectiveDate) throw badRequest('Enter the date this payroll tax registration takes effect.');
    if (!state && !sui) throw badRequest('A Registered location needs at least its state account number (SEIN) or its SUI account number.');
  }
  if (status === 'NotRequired' && !notes) throw badRequest('Enter the compliance reason this location does not need a payroll tax registration.');

  return {
    status,
    effectiveDate: request.effectiveDate,
    nexusEstablishedDate: request.nexusEstablishedDate,
    stateAccountNumberMasked: state,
    suiAccountNumberMasked: sui,
    sdiPflAccountNumberMasked: sdi,
    localTaxRegistrationMasked: localReg,
    localTaxAccountNumberMasked: localAcct,
    notes,
    recordedAtUtc: new Date().toISOString(),
  };
}

/** Why a registration is not complete; empty only for Registered with its effective date and a state account number. */
export function registrationIssues(registration: TaxRegistrationResponse | null): string[] {
  if (!registration || registration.status === 'NotRegistered') return ['Payroll tax registration is not on file for this location.'];
  if (registration.status === 'InProgress') return ['Payroll tax registration is still in progress.'];
  if (registration.status === 'NotRequired') return ['Marked not required. This stays incomplete until an authorized person verifies the exemption.'];
  const issues: string[] = [];
  if (!registration.effectiveDate) issues.push('The registration effective date is missing.');
  if (!registration.stateAccountNumberMasked && !registration.suiAccountNumberMasked) issues.push('No state account number is on file.');
  return issues;
}

/** Writes the registration (or "none") onto the location with the derived fields the API computes. */
export function applyRegistration(location: WorkLocationResponse, registration: TaxRegistrationResponse | null): void {
  location.taxRegistration = registration;
  location.taxRegistrationStatus = registration?.status ?? 'NotRegistered';
  location.registrationIssues = registrationIssues(registration);
  location.registrationComplete = registration?.status === 'Registered' && location.registrationIssues.length === 0;
}

export { TAX_REGISTRATION_LABELS };
